import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ZoomIn, Share2 } from 'lucide-react';
import ImageLightbox from './ImageLightbox';
import { ShareModal } from './product/ShareModal';

const fmt = (price) => `R${Number(price).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function ProductCard({ product, isFav = false, onToggleFav }) {
  const navigate = useNavigate();
  const discount = Math.round(product.discount_percentage || 0);
  const [lightbox, setLightbox] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const images = product.images?.filter(Boolean) || [];

  return (
    <>
      {lightbox && (
        <ImageLightbox
          images={images}
          index={0}
          onClose={() => setLightbox(false)}
          onNav={() => {}}
        />
      )}
      {showShare && (
        <ShareModal product={product} onClose={() => setShowShare(false)} />
      )}
      <div
        className="group cursor-pointer flex flex-col gap-3"
        data-testid={`product-card-${product.product_id}`}
        onClick={() => navigate(`/product/${product.product_id}`)}
      >
      {/* Image */}
      <div className="relative w-full aspect-[4/5] bg-[#F9F9F9] overflow-hidden">
        <img
          src={images[0] || 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=800'}
          alt={product.name}
          className="object-contain w-full h-full group-hover:scale-105 transition-transform duration-700 ease-in-out transform-gpu backface-hidden"
          loading="eager"
          decoding="async"
          fetchpriority="high"
        />

        {/* Zoom / view image button */}
        <button
          className="absolute bottom-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white transition-all opacity-0 group-hover:opacity-100 shadow-sm"
          onClick={(e) => { e.stopPropagation(); setLightbox(true); }}
          data-testid={`zoom-btn-${product.product_id}`}
          aria-label="View image"
        >
          <ZoomIn size={14} className="text-[#0A0A0A]" />
        </button>

        {/* Share to Instagram button */}
        <button
          className="absolute bottom-3 left-3 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white transition-all opacity-0 group-hover:opacity-100 shadow-sm"
          onClick={(e) => { e.stopPropagation(); setShowShare(true); }}
          data-testid={`share-btn-${product.product_id}`}
          aria-label="Share on Instagram"
        >
          <Share2 size={14} className="text-[#0A0A0A]" />
        </button>

        {/* Favorite Button */}
        <button
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white transition-colors shadow-sm"
          onClick={(e) => { e.stopPropagation(); onToggleFav?.(); }}
          data-testid={`fav-btn-${product.product_id}`}
          aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
        >
          <Heart
            size={14}
            className={isFav ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#0A0A0A]"}
          />
        </button>

        {/* Discount Badge */}
        {discount > 0 && (
          <div className="absolute top-3 left-3 bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
            -{discount}%
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex flex-col gap-1">
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#D4AF37]">
          {product.designer}
        </p>
        <p className="text-sm font-medium text-[#0A0A0A] leading-tight line-clamp-2 font-work">
          {product.name}
        </p>
        <div className="flex flex-col gap-0.5 mt-1">
          {product.original_price > product.final_price && (
            <p className="text-[#AAAAAA] line-through text-xs font-work">
              {fmt(product.original_price)}
            </p>
          )}
          <p className="text-[#D4AF37] font-semibold text-base font-work">
            {fmt(product.final_price)}
          </p>
          {product.savings > 0 && (
            <p className="text-[#D4AF37] text-[10px] font-semibold uppercase tracking-wider font-work">
              Save {fmt(product.savings)}
            </p>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
